/**
 * PATH: apps/hr/src/pages/documents/DocumentDetailPage.tsx
 * PURPOSE: Document detail view orchestrator — preview, OCR fields, tags, timeline
 */

import { Link } from 'react-router-dom'
import {
  ArrowLeft, Download, FileText, CheckCircle2, XCircle,
  Trash2, Loader2, Ticket,
} from 'lucide-react'
import { useDocumentDetail, AVAILABLE_TAGS, SUGGESTED_TAGS } from './document-detail/useDocumentDetail'
import { DocumentPreview } from './document-detail/DocumentPreview'
import { StatusBadge, ConfidenceBadge } from './document-detail/DetailBadges'
import { RejectModal, DeleteModal } from './document-detail/DetailModals'
import { ActivityTimeline } from './document-detail/ActivityTimeline'
import { ExtractedFieldsSection } from './document-detail/ExtractedFieldsSection'
import { DocumentSidebar } from './document-detail/DocumentSidebar'
import { CreateRequestModal } from '../../components/support/CreateRequestModal'

export default function DocumentDetailPage() {
  const {
    id, document, isLoading, error, navigate,
    isEditing, setIsEditing, editedFields, setEditedFields, isSaving,
    showRejectModal, setShowRejectModal, showDeleteModal, setShowDeleteModal, isDeleting,
    appliedTags, tagSearch, setTagSearch, showTagDropdown, setShowTagDropdown,
    showCreateRequestModal, setShowCreateRequestModal,
    isExpired, isExpiringSoon,
    handleFieldEdit, handleSaveFields, handleApprove, handleReject, handleDelete,
    handleApplyTag, handleRemoveTag, handleDownload,
  } = useDocumentDetail()

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
  }

  if (error || !document) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/documents')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Document Not Found</h1>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-700 mb-3" />
          <p className="text-slate-500 dark:text-slate-400 mb-4">The document you're looking for doesn't exist or has been deleted.</p>
          <button onClick={() => navigate('/documents')} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">
            Back to Documents
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/documents" className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{document.documentName}</h1>
              <StatusBadge status={document.status} />
              {document.confidenceScore !== undefined && <ConfidenceBadge confidence={document.confidenceScore} />}
            </div>
            <p className="text-slate-500 dark:text-slate-400 mt-1">{document.documentNumber} • {document.employeeName}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {document.status === 'needs_review' && (
            <>
              <button onClick={() => setShowRejectModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20">
                <XCircle className="w-4 h-4" />Reject
              </button>
              <button onClick={handleApprove}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
                <CheckCircle2 className="w-4 h-4" />Approve
              </button>
            </>
          )}
          <button onClick={() => setShowCreateRequestModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
            <Ticket className="w-4 h-4" />Create Support Request
          </button>
          <button onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20">
            <Trash2 className="w-4 h-4" />Delete
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Document Preview</h2>
              <button onClick={handleDownload} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
                <Download className="w-4 h-4" />Download
              </button>
            </div>
            <div className="aspect-[4/3] bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center overflow-hidden relative">
              <DocumentPreview fileUrl={document.fileUrl} fileName={document.fileName} fileType={document.fileType} />
            </div>
          </div>

          <ExtractedFieldsSection
            fields={document.extractedFields || []}
            isEditing={isEditing} editedFields={editedFields} isSaving={isSaving}
            onToggleEdit={() => { if (isEditing) { setIsEditing(false); setEditedFields({}) } else { setIsEditing(true) } }}
            onFieldEdit={handleFieldEdit} onSave={handleSaveFields}
            onCancel={() => { setIsEditing(false); setEditedFields({}) }}
          />

          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Activity Timeline</h2>
            <ActivityTimeline documentId={id!} uploadedAt={document.createdAt} ocrConfidence={document.confidenceScore} />
          </div>
        </div>

        {/* Right Column */}
        <DocumentSidebar
          document={document} isExpired={isExpired} isExpiringSoon={isExpiringSoon}
          appliedTags={appliedTags} suggestedTags={SUGGESTED_TAGS} availableTags={AVAILABLE_TAGS}
          tagSearch={tagSearch} showTagDropdown={showTagDropdown}
          onTagSearchChange={setTagSearch} onShowTagDropdown={setShowTagDropdown}
          onApplyTag={handleApplyTag} onRemoveTag={handleRemoveTag}
        />
      </div>

      {/* Modals */}
      <RejectModal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} onReject={handleReject} />
      <DeleteModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} onDelete={handleDelete} isDeleting={isDeleting} />
      <CreateRequestModal isOpen={showCreateRequestModal} onClose={() => setShowCreateRequestModal(false)}
        preselectedDocument={{ id: document.id, name: document.documentName, employeeId: document.employeeId, employeeName: document.employeeName }} />
    </div>
  )
}
